import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import { ProspectsService } from '../shared/services/prospects.service';


export interface BrandProspectSummaryDTO {
  brandName: string;
  prospectCount: number;
}


@Component({
  selector: 'app-brand-prospect-summary',
  templateUrl: './brand-prospect-summary.component.html',
  styleUrl: './brand-prospect-summary.component.scss'
})
export class BrandProspectSummaryComponent {
 @ViewChild('dtBrandProspectSummary') table: Table;
  @ViewChild('brandProspectChart') brandProspectChart: UIChart;

  brandProspectSummary: BrandProspectSummaryDTO[] = [];
  filteredBrandProspectSummary: BrandProspectSummaryDTO[] = [];
  brandProspectChartData: any;
  brandProspectChartOptions: any;
  
  // Track which individual pie slices are hidden
  hiddenItems: boolean[] = [];

  selectedBrandScope: string = 'all';
  selectedBrandCount: number = 1;
  isSorted: boolean = false;

  brandScopeOptions: any[] = [
    { label: 'Top', value: 'top' },
    { label: 'All', value: 'all' },
    { label: 'Bottom', value: 'bottom' }
  ];

  brandNamesOption: any [] = [];

  constructor(private prospectsService: ProspectsService) {}

  ngOnInit() {
    this.loadBrandProspectSummary();
    this.initializeChartOptions();
  }

  loadBrandProspectSummary() {
    // Update this to call your actual service method for brand data
    this.prospectsService.getBrandProspectSummary()
      .toPromise()
      .then(response => {
        this.brandProspectSummary = response;
        this.filteredBrandProspectSummary = response;
        this.updateChartData();
      })
      .catch(error => {
        console.error('Error loading brand prospect summary:', error);
      });


     
      

    // Simulating API call with fake data - remove this when you have real API
    // setTimeout(() => {
    //   this.brandProspectSummary = [
    //     { brandName: 'Brand A', prospectCount: 150 },
    //     { brandName: 'Brand B', prospectCount: 120 },
    //     { brandName: 'Brand C', prospectCount: 98 },
    //     { brandName: 'Brand D', prospectCount: 85 },
    //     { brandName: 'Brand E', prospectCount: 67 }
    //   ];
    //   this.filteredBrandProspectSummary = this.brandProspectSummary;
    //   this.updateChartData();
    // }, 1000);
  }

  initializeChartOptions() {
    this.brandProspectChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // Hide default legend, we'll use custom one
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const label = tooltipItem.label;
              const value = tooltipItem.raw;
              return `${label}: ${value.toLocaleString()} prospects`;
            }
          }
        }
      }
    };
  }

  updateChartData() {
    let dataToDisplay = [...this.filteredBrandProspectSummary];

    // Apply sorting based on scope or sort toggle
    if (this.selectedBrandScope === 'top' || (this.isSorted && this.selectedBrandScope === 'all')) {
      dataToDisplay.sort((a, b) => b.prospectCount - a.prospectCount);
    } else if (this.selectedBrandScope === 'bottom') {
      dataToDisplay.sort((a, b) => a.prospectCount - b.prospectCount);
    }

    // Limit to selected number of brands if scope is 'top' or 'bottom'
    if (this.selectedBrandScope === 'top' || this.selectedBrandScope === 'bottom') {
      dataToDisplay = dataToDisplay.slice(0, this.selectedBrandCount);
    }

    const labels = dataToDisplay.map(item => item.brandName);
    const data = dataToDisplay.map(item => item.prospectCount);

    // Default colors
    const baseColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#4BC0C0', 
                        '#7986CB', '#FFA726', '#66BB6A', '#26C6DA', '#EC407A'];
    
    // Generate more colors if needed
    const backgroundColor = this.generateColors(baseColors, labels.length);
    const hoverBackgroundColor = [...backgroundColor]; // Same colors for hover

    this.brandProspectChartData = {
      labels: labels,
      datasets: [{
        data: data,
        backgroundColor: backgroundColor,
        hoverBackgroundColor: hoverBackgroundColor
      }]
    };

    // Reset hidden items tracking
    this.hiddenItems = new Array(labels.length).fill(false);

       this.brandNamesOption = this.brandProspectSummary.map(brand => ({
      label: brand.brandName,
      value: brand.brandName
    }));

    if (this.brandProspectChart) {
      this.brandProspectChart.reinit();
    }

    
 
    
  }

  // Helper method to generate colors for the chart
  generateColors(baseColors: string[], count: number): string[] {
    if (count <= baseColors.length) {
      return baseColors.slice(0, count);
    }
    
    // If we need more colors than the base set, generate them by cycling through the base set
    const colors = [...baseColors];
    for (let i = baseColors.length; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  }

  onBrandScopeChange() {
    this.updateChartData();
  }

  onBrandCountChange() {
    // Validate input: ensure it's a positive integer
    if (this.selectedBrandCount < 1 || isNaN(this.selectedBrandCount)) {
      this.selectedBrandCount = 1;
    }
    // Optional: Cap at the number of available brands
    const maxCount = this.filteredBrandProspectSummary.length;
    if (this.selectedBrandCount > maxCount) {
      this.selectedBrandCount = maxCount;
    }
    this.updateChartData();
  }

  onSortToggle() {
    this.isSorted = !this.isSorted;
    this.updateChartData();
  }

  onTableFilter(event: any) {
    this.filteredBrandProspectSummary = event.filteredValue || [];
    this.updateChartData();
  }

  // Toggle visibility of a specific pie chart segment
  toggleLegendItem(itemIndex: number) {
    // Initialize hiddenItems array if needed
    if (!this.hiddenItems || this.hiddenItems.length === 0) {
      this.hiddenItems = new Array(this.brandProspectChartData.labels.length).fill(false);
    }

    // Toggle the hidden state for this item
    this.hiddenItems[itemIndex] = !this.hiddenItems[itemIndex];
    
    if (this.brandProspectChart && this.brandProspectChart.chart) {
      // For pie/doughnut charts, we need to update the meta for the specific data point
      const meta = this.brandProspectChart.chart.getDatasetMeta(0);
      
      if (meta && meta.data && meta.data[itemIndex]) {
        // Toggle the hidden property of the specific slice
        meta.data[itemIndex].hidden = this.hiddenItems[itemIndex];
        
        // Update the chart
        this.brandProspectChart.chart.update();
      }
    }
  }
  
  // Check if a specific pie slice is hidden
  isItemHidden(itemIndex: number): boolean {
    if (!this.hiddenItems || this.hiddenItems.length === 0) {
      return false;
    }
    return this.hiddenItems[itemIndex];
  }

  exportSummaryPdf() {
    const doc = new jsPDF('landscape', 'pt', 'a4');

    const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Brand Name', dataKey: 'brandName' },
      { header: 'Prospect Count', dataKey: 'prospectCount' }
    ];

    const rows = this.filteredBrandProspectSummary.map((row, index) => ({
      index: index + 1,
      brandName: row.brandName,
      prospectCount: row.prospectCount
    }));

    autoTable(doc, {
      columns: columns,
      body: rows,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 10,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        index: { cellWidth: 50, halign: 'center' },
        brandName: { cellWidth: 200, halign: 'center' },
        prospectCount: { cellWidth: 100, halign: 'center' }
      },
      margin: { top: 20 }
    });

    doc.save('BrandProspectSummary.pdf');
  }

  exportSummaryExcel() {
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.filteredBrandProspectSummary.map(summary => ({
        'Brand Name': summary.brandName,
        'Prospect Count': summary.prospectCount
      })));

      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, "BrandProspectSummary");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  // exportChartAsImage() {
  //   if (this.brandProspectChart && this.brandProspectChart.chart) {
  //     const chart = this.brandProspectChart.chart;
  //     const canvas = chart.canvas;
  //     const ctx = canvas.getContext('2d');

  //     ctx.save();
  //     ctx.globalCompositeOperation = 'destination-over';
  //     ctx.fillStyle = '#FFFFFF';
  //     ctx.fillRect(0, 0, canvas.width, canvas.height);

  //     const base64Image = canvas.toDataURL('image/png');
  //     ctx.restore();

  //     const link = document.createElement('a');
  //     link.href = base64Image;
  //     link.download = 'brand-prospect-chart.png';
  //     link.click();
  //   }
  // }
  

  // exportChartAsPDF() {
  //   if (this.brandProspectChart && this.brandProspectChart.chart) {
  //     const chart = this.brandProspectChart.chart;
  //     const canvas = chart.canvas;
  //     const ctx = canvas.getContext('2d');

  //     ctx.save();
  //     ctx.globalCompositeOperation = 'destination-over';
  //     ctx.fillStyle = '#FFFFFF';
  //     ctx.fillRect(0, 0, canvas.width, canvas.height);

  //     const base64Image = canvas.toDataURL('image/png');
  //     ctx.restore();

  //     const imgWidth = 280;
  //     const imgHeight = (canvas.height / canvas.width) * imgWidth;

  //     const pdf = new jsPDF('landscape');
  //     pdf.addImage(base64Image, 'PNG', 10, 10, imgWidth, imgHeight);
  //     pdf.save('brand-prospect-chart.pdf');
  //   }
  // }



  exportChartAsImage() {
  if (this.brandProspectChart && this.brandProspectChart.chart) {
    const chart = this.brandProspectChart.chart;
    
    // Store original legend display setting
    const originalLegendDisplay = chart.options.plugins.legend.display;
    
    // Show the original Chart.js legend
    chart.options.plugins.legend.display = true;
    chart.options.plugins.legend.position = 'right'; 
    chart.update();

    // Wait a bit for the chart to update, then capture it
    setTimeout(() => {
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const link = document.createElement('a');
      link.href = base64Image;
      link.download = 'brand-prospect-chart.png';
      link.click();
      
      // Restore original legend setting (hide it to show custom legend)
      chart.options.plugins.legend.display = originalLegendDisplay;
      chart.update();
    }, 10);
  }
}


    

exportChartAsPDF() {
  if (this.brandProspectChart && this.brandProspectChart.chart) {
    const chart = this.brandProspectChart.chart;
    
    // Store original legend display setting
    const originalLegendDisplay = chart.options.plugins.legend.display;
    
    // Show the original Chart.js legend
    chart.options.plugins.legend.display = true;
    chart.options.plugins.legend.position = 'right'; 
    chart.update();
    
    // Wait a bit for the chart to update, then capture it
    setTimeout(() => {
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const imgWidth = 280;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      const pdf = new jsPDF('landscape');
      pdf.addImage(base64Image, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save('brand-prospect-chart.pdf');
      
      // Restore original legend setting (hide it to show custom legend)
      chart.options.plugins.legend.display = originalLegendDisplay;
      chart.update();
    }, 10);
    }
  }
}
